import {
  IonApp,
  Platform,
  IonProgressBar,
  MenuController,
  IonRouterOutlet,
  ModalController,
  PopoverController,
  ActionSheetController,
} from '@ionic/angular/standalone';
import { takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { Component, Injector, OnInit, inject } from '@angular/core';
import { Intent, WebIntent } from '@awesome-cordova-plugins/web-intent/ngx';

import { ComposeMessagePage } from '@app/pages/messages/compose-message/compose-message.page';
import { FirebaseMessagingService } from '@app/services/firebase-messaging.service';
import { OfflineDialCodesService } from '@app/services/offline-dial-codes.service';
import { OfflineDetectionService } from '@app/services/offline-detection.service';
import { PushNotificationService } from '@app/services/push-notification.service';
import { SseNotificationService } from '@app/services/sse-notification.service';
import { BiometricAuthService } from '@app/services/biometric-auth.service';
import { ComposeMessageInterface } from '@app/interfaces/message.interface';
import { LabelMetadataService } from '@app/services/label-metadata.service';
import { LabelReportsService } from '@app/services/label-reports.service';
import { CallerBlocksService } from '@app/services/caller-blocks.service';
import { MenuComponent } from '@app/components/menu/menu.component';
import { ContactsService } from '@app/services/contacts.service';
import { OverlayService } from '@app/services/overlay.service';
import { SocketService } from '@app/services/socket.service';
import { StatusService } from '@app/services/status.service';
import { LabelsService } from '@app/services/labels.service';
import { LoaderService } from '@app/services/loader.service';
import { BadgeService } from '@app/services/badge.service';
import { UserService } from '@app/services/user.service';
import { DialService } from '@app/services/dial.service';
import { Events } from '@app/services/events.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonRouterOutlet, MenuComponent, AsyncPipe, IonApp, IonProgressBar],
})
export class AppComponent implements OnInit {
  overlay = inject(OverlayService);
  loaderSrv = inject(LoaderService);
  bioAuthSrv = inject(BiometricAuthService);
  private events = inject(Events);
  private router = inject(Router);
  private injector = inject(Injector);
  private platform = inject(Platform);
  private dialSrv = inject(DialService);
  private userSrv = inject(UserService);
  private socket = inject(SocketService);
  private badgeSrv = inject(BadgeService);
  private statusSrv = inject(StatusService);
  private menuCtrl = inject(MenuController);
  private labelsSrv = inject(LabelsService);
  private modalCtrl = inject(ModalController);
  private contactsSrv = inject(ContactsService);
  private popoverCtrl = inject(PopoverController);
  private callerBlocksSrv = inject(CallerBlocksService);
  private labelReportsSrv = inject(LabelReportsService);
  private actionSheetCtrl = inject(ActionSheetController);
  private labelMetadataSrv = inject(LabelMetadataService);
  private sseNotificationSrv = inject(SseNotificationService);
  private offlineDetectionSrv = inject(OfflineDetectionService);
  private offlineDialCodesSrv = inject(OfflineDialCodesService);
  private pushNotificationSrv = inject(PushNotificationService);
  private firebaseMessagingSrv = inject(FirebaseMessagingService);

  private webIntent?: WebIntent;

  async ngOnInit() {
    await this.initializeApp();
    this.listenBackButtonEvent();
  }

  async initializeApp() {
    await this.platform.ready();
    this.subscribeEvents();

    await SplashScreen.hide();
    this.badgeSrv.initBadge();

    this.checkUser();

    if (Capacitor.isNativePlatform()) {
      // Initialize Web Intent only on native platforms
      this.webIntent = this.injector.get(WebIntent);
      try {
        const intent = await this.webIntent.getIntent();
        this.handleIntent(intent);
      } catch (err) {
        console.log('error get intent', err);
      }
      this.webIntent.onIntent().subscribe((intent) => {
        this.handleIntent(intent);
      });
    }
  }

  listenBackButtonEvent() {
    this.platform.backButton.subscribe(async () => {
      if (
        (await this.closeTopController(this.actionSheetCtrl)) ||
        (await this.closeTopController(this.popoverCtrl)) ||
        (await this.closeTopController(this.modalCtrl)) ||
        (await this.closeTopController(this.menuCtrl))
      ) {
        return;
      }
      window.history.back();
    });
  }

  subscribeEvents() {
    // event from user form
    this.events.subscribe('user:login', (data?: { redirectTo: string }) => {
      this.userSrv.getRpcHash().subscribe(() => this.checkUser(data?.redirectTo));
    });

    this.events.subscribe('socket:login', () => {
      this.getStatus();
    });

    this.events.subscribe('contact:composeMessage', (contact?: ComposeMessageInterface) => {
      if (contact) {
        this.composeMessage(contact);
      }
    });
  }

  checkUser(redirectTo?: string) {
    const token = this.userSrv.accessToken;
    if (token) {
      this.userSrv.getMyAccount().subscribe({
        next: async () => {
          await this.initProcess();
        },
        error: async (error) => {
          console.log('error with connection to server during check user', error);
          this.offlineDetectionSrv.setOfflineMode(true);
          await this.initProcess();
        },
        complete: () => {
          if (redirectTo) {
            this.router.navigateByUrl(redirectTo);
          }
        },
      });
    }
  }

  async initProcess() {
    await this.bioAuthSrv.authenticate();
    this.platform.resume.pipe(takeUntil(this.userSrv.logout$)).subscribe(() => this.bioAuthSrv.authenticate());

    if (this.userSrv.isAdmin()) {
      await this.pushNotificationSrv.initNotification();
      this.sseNotificationSrv.connect();
    }
    this.getStatus();
    this.socket
      .getMessage('statusUpdated')
      .pipe(takeUntil(this.userSrv.logout$))
      .subscribe(() => this.getStatus());

    await this.contactsSrv.initContacts();
    if (this.userSrv.isAdmin()) {
      this.labelReportsSrv.init();
      this.offlineDialCodesSrv.fetchOurGsmNumbers();
      this.callerBlocksSrv.fetchCallerBlockReasons();
      await this.firebaseMessagingSrv.initNotifications();
    }
    this.labelsSrv.requestLabelData();
    this.labelMetadataSrv.fetchAllMetadataColumns();
  }

  async composeMessage(contact: ComposeMessageInterface) {
    const messageModal = await this.modalCtrl.create({
      component: ComposeMessagePage,
      componentProps: { contact },
      backdropDismiss: false,
    });
    await messageModal.present();
  }

  handleIntent(intent: Intent) {
    if (
      ['android.intent.action.CALL', 'android.intent.action.DIAL', 'android.intent.action.VIEW'].includes(intent.action)
    ) {
      this.makeCallFromIntent(intent);
    } else {
      this.composeMessageFromIntent(intent);
    }
  }

  async composeMessageFromIntent(intent: Intent) {
    if (intent.type !== 'text/plain' || !intent.extras) {
      return;
    }
    const extras: any = intent.extras;
    const subject = extras['android.intent.extra.SUBJECT'] || '';
    const text = extras['android.intent.extra.TEXT'] || '';
    const message = `${subject}\n${text}`;
    const messageModal = await this.modalCtrl.create({
      component: ComposeMessagePage,
      componentProps: {
        message,
        isSelectContact: true,
      },
      backdropDismiss: false,
    });
    await messageModal.present();
  }

  makeCallFromIntent(intent: any) {
    if (!intent.data) {
      return;
    }
    const data = decodeURIComponent(intent.data);
    let phone = data.replace(/^tel:/, '');
    phone = this.dialSrv.normalizeNumber(phone);
    this.dialSrv.showDialPad(phone);
  }

  private async closeTopController(
    controller: ActionSheetController | MenuController | PopoverController | ModalController,
  ) {
    try {
      if (controller instanceof MenuController) {
        const element = await controller.getOpen();
        if (element) {
          controller.close();
          return true;
        }
      } else {
        const element = await controller.getTop();
        if (element) {
          element.dismiss();
          return true;
        }
      }
    } catch (error) {
      console.log('error close controller', error);
    }
    return false;
  }

  private getStatus() {
    this.statusSrv.getStatus().subscribe({ error: (error) => console.log('error get status', error) });
  }
}
